// src/pages/Privacy.tsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Lock,
  Eye,
  Database,
  UserCheck,
  Mail,
  Sparkles,
  Key,
  Globe,
  Cpu,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/privacy" />

      {/* Header */}
      <section className="relative overflow-hidden">
        {/* Background Glow Effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-5 py-20 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm">
              <Shield className="h-4 w-4 mr-2" />
              Privacy & Security
            </div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-8">
              Privacy Policy
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-12">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {/* Introduction */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Lock className="h-6 w-6 text-primary" />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                      1. Introduction
                    </h2>
                  </div>
                  <div className="space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      Welcome to SyvronRecovery. We are committed to protecting
                      your personal information and your right to privacy. If
                      you have any questions or concerns about this privacy
                      policy, or our practices with regards to your personal
                      information, please contact us at
                      privacy@syvronrecovery.com.
                    </p>
                    <p>
                      This Privacy Policy describes how we collect, use, store,
                      and share your personal information when you use our
                      services, which include our website, mobile applications,
                      and related services (collectively, the "Services").
                    </p>
                  </div>
                </div>
              </div>

              {/* Information We Collect */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Database className="h-6 w-6 text-blue-500" />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                      2. Information We Collect
                    </h2>
                  </div>
                  <div className="space-y-6">
                    <p className="text-gray-600 dark:text-gray-400">
                      We collect personal information that you voluntarily
                      provide to us when you register for our Services, express
                      an interest in obtaining information about us or our
                      products and Services, or otherwise when you contact us.
                    </p>

                    {/* Personal Information Card */}
                    <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/20">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <UserCheck className="h-5 w-5 text-primary" />
                        Personal Information You Provide
                      </h3>
                      <div className="space-y-3">
                        {[
                          "Account Information: Name, email address, username, and password",
                          "Wallet Information: Public wallet addresses (no private keys)",
                          "Verification Documents: For KYC verification purposes",
                          "Transaction Data: Information related to deposits, withdrawals, and recovery requests",
                          "Communication Data: Messages, support tickets, and other communications",
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start text-sm">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-700 dark:text-gray-300">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Automatic Information Card */}
                    <div className="bg-gradient-to-br from-blue-500/5 to-blue-500/10 rounded-xl p-6 border border-blue-500/20">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Cpu className="h-5 w-5 text-blue-500" />
                        Information Automatically Collected
                      </h3>
                      <div className="space-y-3">
                        {[
                          "Device Information: IP address, browser type, device type, operating system",
                          "Usage Data: Pages visited, features used, time spent on platform",
                          "Location Data: General location based on IP address for security",
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start text-sm">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-700 dark:text-gray-300">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* How We Use Information */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-500/10 via-green-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Eye className="h-6 w-6 text-green-500" />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                      3. How We Use Your Information
                    </h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    We use the information we collect for various business
                    purposes described below:
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      {
                        title: "Service Delivery",
                        description:
                          "To provide and maintain our Services, process transactions, and manage your account.",
                        icon: Key,
                        color: "from-primary/10 to-primary/5",
                        border: "border-primary/20",
                      },
                      {
                        title: "Security & Protection",
                        description:
                          "To protect against fraudulent activity, monitor for security threats, and verify identity.",
                        icon: Shield,
                        color: "from-green-500/10 to-green-500/5",
                        border: "border-green-500/20",
                      },
                      {
                        title: "Communication",
                        description:
                          "To send important updates, security alerts, and respond to your inquiries.",
                        icon: Mail,
                        color: "from-blue-500/10 to-blue-500/5",
                        border: "border-blue-500/20",
                      },
                      {
                        title: "Legal Compliance",
                        description:
                          "To comply with legal obligations, regulations, and law enforcement requests.",
                        icon: Globe,
                        color: "from-purple-500/10 to-purple-500/5",
                        border: "border-purple-500/20",
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className={`bg-gradient-to-br ${item.color} rounded-xl p-5 border ${item.border} group hover:scale-102 transition-all duration-300`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <item.icon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                          <h4 className="font-bold text-gray-900 dark:text-white">
                            {item.title}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Data Sharing */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/10 via-yellow-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <UserCheck className="h-6 w-6 text-yellow-500" />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                      4. Data Sharing & Disclosure
                    </h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    We do not sell, rent, or trade your personal information to
                    third parties. We may share information only in the
                    following circumstances:
                  </p>

                  <div className="space-y-4">
                    {[
                      {
                        title: "Service Providers",
                        description:
                          "With trusted third-party providers who assist us in operating our Services (e.g., hosting, payment processing, customer support).",
                        bgColor: "from-yellow-500/10 to-yellow-500/5",
                        borderColor: "border-yellow-500/20",
                      },
                      {
                        title: "Legal Requirements",
                        description:
                          "When required by law, regulation, or legal process, or to protect our rights, property, or safety.",
                        bgColor: "from-red-500/10 to-red-500/5",
                        borderColor: "border-red-500/20",
                      },
                      {
                        title: "Business Transfers",
                        description:
                          "In connection with a merger, acquisition, or sale of assets, where your information may be transferred as part of the transaction.",
                        bgColor: "from-green-500/10 to-green-500/5",
                        borderColor: "border-green-500/20",
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className={`bg-gradient-to-br ${item.bgColor} rounded-xl p-5 border ${item.borderColor} group hover:scale-102 transition-all duration-300`}
                      >
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Data Security */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield className="h-6 w-6 text-primary" />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                      5. Data Security
                    </h2>
                  </div>
                  <div className="space-y-6">
                    <p className="text-gray-600 dark:text-gray-400">
                      We implement appropriate technical and organizational
                      security measures designed to protect the security of any
                      personal information we process. However, no electronic
                      transmission over the Internet or information storage
                      technology can be guaranteed to be 100% secure.
                    </p>

                    <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/20">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        Our Security Measures Include:
                      </h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          "End-to-end encryption for sensitive data",
                          "Regular security audits and penetration testing",
                          "Multi-factor authentication for account access",
                          "Secure data centers with physical security",
                          "Employee training on data protection",
                          "Real-time threat monitoring systems",
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <div className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></div>
                            <span className="text-gray-700 dark:text-gray-300">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Your Rights */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-500/10 via-green-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    6. Your Privacy Rights
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      {
                        title: "Access & Correction",
                        description:
                          "You have the right to access and update your personal information through your account settings.",
                        color: "from-green-500/10 to-green-500/5",
                        border: "border-green-500/20",
                      },
                      {
                        title: "Data Portability",
                        description:
                          "You can request a copy of your personal data in a structured, machine-readable format.",
                        color: "from-blue-500/10 to-blue-500/5",
                        border: "border-blue-500/20",
                      },
                      {
                        title: "Deletion Rights",
                        description:
                          "You may request deletion of your personal information, subject to certain legal exceptions.",
                        color: "from-red-500/10 to-red-500/5",
                        border: "border-red-500/20",
                      },
                      {
                        title: "Opt-Out Rights",
                        description:
                          "You can opt out of marketing communications at any time by updating your preferences.",
                        color: "from-purple-500/10 to-purple-500/5",
                        border: "border-purple-500/20",
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className={`bg-gradient-to-br ${item.color} rounded-xl p-5 border ${item.border} group hover:scale-102 transition-all duration-300`}
                      >
                        <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Mail className="h-6 w-6 text-blue-500" />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                      7. Contact Us
                    </h2>
                  </div>
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-8 border border-primary/20">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      If you have questions or comments about this Privacy
                      Policy, you may contact our Data Protection Officer at:
                    </p>
                    <div className="space-y-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        Email: privacy@syvronrecovery.com
                      </p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Address: 123 Security Street, Crypto Valley, CV 10001
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                        We will respond to your request within 30 days.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Updates */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    8. Updates to This Policy
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    We may update this Privacy Policy from time to time. The
                    updated version will be indicated by an updated "Last
                    updated" date and the updated version will be effective as
                    soon as it is accessible. We encourage you to review this
                    Privacy Policy frequently to be informed of how we are
                    protecting your information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="SyvronRecovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  SyvronRecovery
                </span>
              </div>
              <p className="mb-4 text-sm">
                Advanced crypto protection and recovery services combining AI
                technology with expert human oversight.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/wallet"
                    className="hover:text-white transition-colors"
                  >
                    Wallet Protection
                  </Link>
                </li>
                <li>
                  <Link
                    to="/recovery"
                    className="hover:text-white transition-colors"
                  >
                    Fund Recovery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard"
                    className="hover:text-white transition-colors"
                  >
                    Dashboard
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2 text-sm">privacy@syvronrecovery.com</p>
              <p className="text-sm">24/7 Expert Support Available</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>
              © {new Date().getFullYear()} SyvronRecovery. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
